import {
  type ReactNode,
  type HTMLAttributes,
  type KeyboardEvent,
  useRef,
  useEffect,
  useCallback,
} from 'react'
import { cn } from '@lib/utils'

export interface SkipLinkProps extends HTMLAttributes<HTMLAnchorElement> {
  /** Target element ID to skip to */
  targetId: string
  /** Link text */
  children?: ReactNode
}

/**
 * Skip link for keyboard navigation
 * Shows only when focused, allowing keyboard users to skip navigation
 *
 * @example
 * <SkipLink targetId="main-content">Skip to main content</SkipLink>
 */
export function SkipLink({
  targetId,
  children = 'Skip to main content',
  className,
  ...props
}: SkipLinkProps) {
  return (
    <a
      href={`#${targetId}`}
      className={cn(
        'fixed top-4 left-4 z-[9999]',
        'bg-primary text-background px-4 py-2 font-bold',
        'border-2 border-background',
        'shadow-brutalist-lg',
        // Hidden by default, visible on focus
        'opacity-0 -translate-y-full pointer-events-none',
        'focus:opacity-100 focus:translate-y-0 focus:pointer-events-auto',
        'transition-all duration-200',
        className
      )}
      {...props}
    >
      {children}
    </a>
  )
}

export interface FocusTrapProps extends HTMLAttributes<HTMLDivElement> {
  /** Content to trap focus within */
  children: ReactNode
  /** Whether focus trap is active */
  active?: boolean
  /** Callback when escape is pressed */
  onEscape?: () => void
  /** Return focus to element when deactivated */
  returnFocus?: boolean
}

/**
 * Focus trap component
 * Traps keyboard focus within its children
 *
 * @example
 * <FocusTrap active={isModalOpen} onEscape={closeModal}>
 *   <Modal />
 * </FocusTrap>
 */
export function FocusTrap({
  children,
  active = true,
  onEscape,
  returnFocus = true,
  className,
  ...props
}: FocusTrapProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const previousActiveElement = useRef<HTMLElement | null>(null)

  // Store previously focused element
  useEffect(() => {
    if (active) {
      previousActiveElement.current = document.activeElement as HTMLElement
    }
  }, [active])

  // Focus first focusable element
  useEffect(() => {
    if (!active || !containerRef.current) return

    const focusableElements = getFocusableElements(containerRef.current)
    if (focusableElements.length > 0) {
      focusableElements[0].focus()
    }

    return () => {
      if (returnFocus && previousActiveElement.current) {
        previousActiveElement.current.focus()
      }
    }
  }, [active, returnFocus])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (!active) return

      if (e.key === 'Escape' && onEscape) {
        e.preventDefault()
        onEscape()
        return
      }

      if (e.key !== 'Tab' || !containerRef.current) return

      const focusableElements = getFocusableElements(containerRef.current)
      if (focusableElements.length === 0) return

      const firstElement = focusableElements[0]
      const lastElement = focusableElements[focusableElements.length - 1]

      // Shift + Tab from first element -> go to last
      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault()
        lastElement.focus()
      }
      // Tab from last element -> go to first
      else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault()
        firstElement.focus()
      }
    },
    [active, onEscape]
  )

  return (
    <div
      ref={containerRef}
      onKeyDown={handleKeyDown}
      className={className}
      {...props}
    >
      {children}
    </div>
  )
}

/**
 * Get all focusable elements within a container
 */
function getFocusableElements(container: HTMLElement): HTMLElement[] {
  const selector = [
    'a[href]',
    'button:not([disabled])',
    'textarea:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(', ')

  return Array.from(container.querySelectorAll<HTMLElement>(selector)).filter(
    (el) => !el.hasAttribute('disabled') && el.offsetParent !== null
  )
}

export interface VisuallyHiddenProps extends HTMLAttributes<HTMLSpanElement> {
  /** Content visible only to screen readers */
  children: ReactNode
  /** Make focusable (shows when focused) */
  focusable?: boolean
}

/**
 * Visually hide content but keep it accessible to screen readers
 *
 * @example
 * <VisuallyHidden>Open navigation menu</VisuallyHidden>
 *
 * <VisuallyHidden focusable>
 *   <a href="#main">Skip to main content</a>
 * </VisuallyHidden>
 */
export function VisuallyHidden({
  children,
  focusable = false,
  className,
  ...props
}: VisuallyHiddenProps) {
  return (
    <span
      className={cn(
        'absolute w-px h-px p-0 -m-px overflow-hidden',
        'whitespace-nowrap border-0',
        '[clip:rect(0,0,0,0)]',
        focusable && 'focus:static focus:w-auto focus:h-auto focus:m-0 focus:overflow-visible focus:clip-auto',
        className
      )}
      {...props}
    >
      {children}
    </span>
  )
}

export interface LiveRegionProps extends HTMLAttributes<HTMLDivElement> {
  /** Politeness level */
  politeness?: 'polite' | 'assertive' | 'off'
  /** Atomic announcement */
  atomic?: boolean
  /** Relevant changes to announce */
  relevant?: 'additions' | 'removals' | 'text' | 'all'
}

/**
 * ARIA live region for dynamic announcements
 *
 * @example
 * <LiveRegion politeness="polite">
 *   {statusMessage}
 * </LiveRegion>
 */
export function LiveRegion({
  children,
  politeness = 'polite',
  atomic = true,
  relevant = 'additions',
  className,
  ...props
}: LiveRegionProps) {
  return (
    <div
      aria-live={politeness}
      aria-atomic={atomic}
      aria-relevant={relevant}
      className={cn('sr-only', className)}
      {...props}
    >
      {children}
    </div>
  )
}

export interface AnnouncerProps {
  /** Message to announce */
  message: string
  /** Politeness level */
  politeness?: 'polite' | 'assertive'
}

/**
 * Screen reader announcer hook
 * Announces messages to screen readers
 *
 * @example
 * const { announce } = useAnnouncer()
 * announce('Item added to cart')
 */
export function useAnnouncer() {
  const announce = useCallback((message: string, politeness: 'polite' | 'assertive' = 'polite') => {
    const announcer = document.getElementById(`announcer-${politeness}`)
    if (announcer) {
      // Clear and re-set to trigger announcement
      announcer.textContent = ''
      requestAnimationFrame(() => {
        announcer.textContent = message
      })
    }
  }, [])

  return { announce }
}

/**
 * Announcer provider component
 * Renders hidden live regions for announcements
 */
export function AnnouncerProvider({ children }: { children: ReactNode }) {
  return (
    <>
      {children}
      <div
        id="announcer-polite"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      />
      <div
        id="announcer-assertive"
        aria-live="assertive"
        aria-atomic="true"
        className="sr-only"
      />
    </>
  )
}

export interface FocusRingProps extends HTMLAttributes<HTMLDivElement> {
  /** Content to wrap with focus ring */
  children: ReactNode
  /** Focus ring color */
  color?: 'primary' | 'white' | 'error'
  /** Focus ring offset */
  offset?: 'none' | 'sm' | 'md'
}

const ringColors = {
  primary: 'focus-within:ring-primary',
  white: 'focus-within:ring-white',
  error: 'focus-within:ring-error',
}

const ringOffsets = {
  none: 'focus-within:ring-offset-0',
  sm: 'focus-within:ring-offset-1',
  md: 'focus-within:ring-offset-2',
}

/**
 * Focus ring wrapper
 * Adds consistent focus styling to wrapped elements
 *
 * @example
 * <FocusRing>
 *   <input type="text" />
 * </FocusRing>
 */
export function FocusRing({
  children,
  color = 'primary',
  offset = 'sm',
  className,
  ...props
}: FocusRingProps) {
  return (
    <div
      className={cn(
        'focus-within:ring-2',
        ringColors[color],
        ringOffsets[offset],
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
}

/**
 * Hook for reduced motion preference
 * Returns true if user prefers reduced motion
 */
export function useReducedMotion(): boolean {
  if (typeof window === 'undefined') return false

  const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
  return mediaQuery.matches
}

export default {
  SkipLink,
  FocusTrap,
  VisuallyHidden,
  LiveRegion,
  AnnouncerProvider,
  FocusRing,
}
